/**
 * Skills Sync Command
 * Sync skills from .jai1/skills/ to IDE directories
 * Supports headless mode for automation
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { confirm, checkbox } from '@inquirer/prompts';
import { SkillsService } from '../../services/skills.service.js';
import { checkboxTheme } from '../../utils/prompt-theme.js';

export function createSkillsSyncCommand(): Command {
    return new Command('sync')
        .description('Sync skills from .jai1/skills/ to IDE directories')
        .option('--ides <ides...>', 'Target IDEs (cursor, windsurf, antigravity, claude, opencode, codex)')
        .option('--skills <skills...>', 'Specific skill slugs to sync (default: all)')
        .option('--all', 'Select all available IDEs')
        .option('--dry-run', 'Preview changes without writing files')
        .option('-y, --yes', 'Headless mode (skip all prompts)')
        .action(async (options: {
            ides?: string[];
            skills?: string[];
            all?: boolean;
            dryRun?: boolean;
            yes?: boolean;
        }) => {
            const skillsService = new SkillsService();
            const projectRoot = process.cwd();
            const headless = options.yes === true;

            console.log(chalk.bold.cyan('\n🔄 Sync skills sang IDE(s)\n'));

            // Check local skills
            const localSkills = await skillsService.listLocal(projectRoot);

            if (localSkills.length === 0) {
                console.log(chalk.yellow('⚠️  Không có skills nào trong .jai1/skills/'));
                console.log(chalk.dim('💡 Chạy "j skills add <tên>" để cài đặt skills trước'));
                process.exit(1);
            }

            console.log(`📁 Tìm thấy ${localSkills.length} skill(s) trong .jai1/skills/`);
            console.log();

            // Select skills
            let selectedSlugs: string[] | undefined;
            if (options.skills && options.skills.length > 0) {
                selectedSlugs = options.skills;
                console.log(`🛠 Skills: ${selectedSlugs.join(', ')}`);
            } else {
                // Sync all skills
                console.log(`🛠 Skills: tất cả (${localSkills.length})`);
            }

            // IDE alias map for backward compatibility
            const ideAliases: Record<string, string> = {
                'claudecode': 'claude',
                'codexcli': 'codex',
                'openai-codex': 'codex',
            };
            const normalizeIde = (ide: string): string => ideAliases[ide] ?? ide;

            // Select IDEs
            let selectedIdes: string[];
            const targets = skillsService.getIDETargets();

            if (options.all) {
                selectedIdes = targets.map(t => t.id);
            } else if (options.ides && options.ides.length > 0) {
                selectedIdes = options.ides.map(normalizeIde);
            } else if (headless) {
                throw new Error(
                    'Headless mode (-y) yêu cầu --ides hoặc --all.\n' +
                    'Ví dụ:\n' +
                    '  j skills sync --ides cursor windsurf -y\n' +
                    '  j skills sync --all -y'
                );
            } else {
                selectedIdes = await checkbox({
                    message: 'Chọn IDE(s) để sync (SPACE để chọn, ENTER để xác nhận):',
                    choices: targets.map(t => ({
                        name: `${t.icon} ${t.name}`,
                        value: t.id,
                    })),
                    theme: checkboxTheme,
                });

                if (selectedIdes.length === 0) {
                    console.log(chalk.yellow('\n⚠️  Chưa chọn IDE nào!'));
                    process.exit(0);
                }
            }

            // Preview
            const ideNames = selectedIdes
                .map(id => targets.find(t => t.id === id))
                .filter(Boolean)
                .map(t => t!.name);

            const skillCount = selectedSlugs ? selectedSlugs.length : localSkills.length;
            const totalFiles = skillCount * selectedIdes.length;

            console.log(`\n📊 Preview:\n`);
            console.log(`   IDEs: ${ideNames.join(', ')}`);
            console.log(`   Skills: ${skillCount}`);
            console.log(`   Total: ${totalFiles} skill folder(s) sẽ được sync\n`);

            if (options.dryRun) {
                console.log(chalk.dim('🔍 DRY RUN - Không có file nào được ghi\n'));
                return;
            }

            // Confirm
            if (!headless) {
                const confirmed = await confirm({
                    message: 'Tiếp tục sync?',
                    default: true,
                });

                if (!confirmed) {
                    console.log(chalk.yellow('\n❌ Đã hủy sync.\n'));
                    process.exit(0);
                }
            }

            // Execute sync
            console.log(chalk.cyan('\n🔄 Đang sync...\n'));

            const result = await skillsService.syncToIdes(
                projectRoot,
                selectedIdes,
                selectedSlugs,
                (res) => {
                    const icon = res.status === 'created' ? '✓' :
                        res.status === 'updated' ? '↻' : '✗';
                    const statusColor = res.status === 'error' ? chalk.red : chalk.green;
                    console.log(`   ${statusColor(icon)} ${res.ide}: ${res.skill} → ${chalk.dim(res.path)}`);
                    if (res.status === 'error' && res.error) {
                        console.log(`      ${chalk.red('Error:')} ${res.error}`);
                    }
                }
            );

            // Summary
            console.log(chalk.green('\n✅ Sync hoàn tất!\n'));
            console.log(`   Created: ${result.created}`);
            console.log(`   Updated: ${result.updated}`);
            if (result.errors > 0) {
                console.log(`   Errors:  ${result.errors}`);
            }
            console.log();
        });
}
